import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminService } from '../../services/adminService';
import './AdminCoupons.css';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    validFrom: '',
    validUntil: '',
    maxTotalUses: '',
    maxUsesPerUser: '1',
    minOrderAmount: '',
    priority: '0',
    benefitType: 'PERCENTAGE_DISCOUNT',
    benefitValue: '',
    benefitMaxValue: '',
    freeDelivery: false,
  });

  useEffect(() => {
    fetchCoupons();
  }, [statusFilter, page]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminService.getCoupons(
        page, 20,
        statusFilter === 'ALL' ? null : statusFilter
      );
      if (response.success) {
        const data = response.data?.content || response.data;
        setCoupons(Array.isArray(data) ? data : []);
        setTotalPages(response.data?.totalPages || 1);
      } else {
        setError(response.message || 'Failed to load coupons');
      }
    } catch (err) {
      console.error('Error fetching coupons:', err);
      setError('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      code: '',
      name: '',
      description: '',
      validFrom: '',
      validUntil: '',
      maxTotalUses: '',
      maxUsesPerUser: '1',
      minOrderAmount: '',
      priority: '0',
      benefitType: 'PERCENTAGE_DISCOUNT',
      benefitValue: '',
      benefitMaxValue: '',
      freeDelivery: false,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingCoupon(null);
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    const hasFreeDelivery = coupon.benefits?.some(b => b.type === 'FREE_DELIVERY') || false;
    const primaryBenefit = coupon.benefits?.find(b => b.type !== 'FREE_DELIVERY') || { type: 'PERCENTAGE_DISCOUNT' };
    setForm({
      code: coupon.code || '',
      name: coupon.name || '',
      description: coupon.description || '',
      validFrom: coupon.validFrom ? coupon.validFrom.slice(0, 16) : '',
      validUntil: coupon.validUntil ? coupon.validUntil.slice(0, 16) : '',
      maxTotalUses: coupon.maxTotalUses?.toString() || '',
      maxUsesPerUser: coupon.maxUsesPerUser?.toString() || '1',
      minOrderAmount: coupon.minOrderAmount?.toString() || '',
      priority: coupon.priority?.toString() || '0',
      benefitType: primaryBenefit.type || 'PERCENTAGE_DISCOUNT',
      benefitValue: primaryBenefit.value?.toString() || '',
      benefitMaxValue: primaryBenefit.maxValue?.toString() || '',
      freeDelivery: hasFreeDelivery,
    });
    setEditingCoupon(coupon);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const needsValue = form.benefitType !== 'FREE_DELIVERY';
    if (!form.code.trim() || !form.name.trim() || (needsValue && !form.benefitValue && !form.freeDelivery)) return;

    setSaving(true);
    try {
      const benefits = [];
      if (form.benefitValue && parseFloat(form.benefitValue) > 0) {
        benefits.push({
          type: form.benefitType,
          value: parseFloat(form.benefitValue),
          ...(form.benefitMaxValue && form.benefitType === 'PERCENTAGE_DISCOUNT'
            ? { maxValue: parseFloat(form.benefitMaxValue) }
            : {}),
        });
      }
      if (form.freeDelivery) {
        benefits.push({ type: 'FREE_DELIVERY', value: 0 });
      }

      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        validFrom: form.validFrom || null,
        validUntil: form.validUntil || null,
        maxTotalUses: form.maxTotalUses ? parseInt(form.maxTotalUses) : null,
        maxUsesPerUser: parseInt(form.maxUsesPerUser) || 1,
        minOrderAmount: form.minOrderAmount ? parseFloat(form.minOrderAmount) : null,
        priority: parseInt(form.priority) || 0,
        benefits,
        conditions: [],
      };

      if (editingCoupon) {
        await adminService.updateCoupon(editingCoupon.id, payload);
      } else {
        await adminService.createCoupon(payload);
      }

      setShowModal(false);
      resetForm();
      fetchCoupons();
    } catch (err) {
      console.error('Error saving coupon:', err);
      alert(err.response?.data?.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (couponId) => {
    try {
      await adminService.deleteCoupon(couponId);
      setShowDeleteConfirm(null);
      fetchCoupons();
    } catch (err) {
      console.error('Error deleting coupon:', err);
      alert(err.response?.data?.message || 'Failed to delete coupon');
    }
  };

  const handleToggleStatus = async (coupon) => {
    try {
      const newStatus = coupon.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await adminService.updateCoupon(coupon.id, { status: newStatus });
      fetchCoupons();
    } catch (err) {
      console.error('Error toggling status:', err);
    }
  };

  const getBenefitDisplay = (benefits) => {
    if (!benefits || benefits.length === 0) return '-';
    const parts = [];
    const hasFreeDelivery = benefits.some(b => b.type === 'FREE_DELIVERY');
    const primary = benefits.find(b => b.type !== 'FREE_DELIVERY');
    if (primary && primary.value > 0) {
      if (primary.type === 'PERCENTAGE_DISCOUNT') {
        parts.push(`${primary.value}% off${primary.maxValue ? ` (max ₹${primary.maxValue})` : ''}`);
      } else if (primary.type === 'FLAT_DISCOUNT') {
        parts.push(`₹${primary.value} off`);
      }
    }
    if (hasFreeDelivery) {
      parts.push('Free Delivery');
    }
    return parts.length > 0 ? parts.join(' + ') : '-';
  };

  const statusOptions = [
    { value: 'ALL', label: 'All' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'EXPIRED', label: 'Expired' },
  ];

  if (loading) {
    return <div className="admin-coupons"><div className="admin-loading">Loading...</div></div>;
  }

  if (error) {
    return (
      <div className="admin-coupons">
        <div className="admin-error">
          <p>{error}</p>
          <button onClick={fetchCoupons} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-coupons">
      <div className="coupons-header">
        <div className="coupons-filters">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              className={`filter-btn ${statusFilter === option.value ? 'active' : ''}`}
              onClick={() => { setStatusFilter(option.value); setPage(0); }}
            >
              {option.label}
            </button>
          ))}
        </div>
        <button className="create-coupon-btn" onClick={openCreateModal}>
          + Create Coupon
        </button>
      </div>

      <div className="coupons-table-container">
        <table className="coupons-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Discount</th>
              <th>Usage</th>
              <th>Min Order</th>
              <th>Valid Until</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr><td colSpan="8" className="no-data">No coupons found</td></tr>
            ) : (
              coupons.map((coupon) => (
                <motion.tr
                  key={coupon.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="coupon-row"
                >
                  <td className="coupon-code">{coupon.code}</td>
                  <td>{coupon.name}</td>
                  <td className="coupon-discount">{getBenefitDisplay(coupon.benefits)}</td>
                  <td>
                    <span className="usage-display">
                      {coupon.currentUses}/{coupon.maxTotalUses || '∞'}
                    </span>
                  </td>
                  <td>{coupon.minOrderAmount ? `₹${coupon.minOrderAmount}` : '-'}</td>
                  <td>
                    {coupon.validUntil
                      ? new Date(coupon.validUntil).toLocaleDateString()
                      : 'No expiry'}
                  </td>
                  <td>
                    <span className={`status-badge status-${coupon.status?.toLowerCase()}`}>
                      {coupon.status}
                    </span>
                  </td>
                  <td className="coupon-actions">
                    <button
                      className="toggle-btn"
                      onClick={() => handleToggleStatus(coupon)}
                      title={coupon.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                    >
                      {coupon.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                    </button>
                    <button className="edit-btn" onClick={() => openEditModal(coupon)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => setShowDeleteConfirm(coupon)}>
                      Delete
                    </button>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            Previous
          </button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content coupon-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-field">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="e.g. WELCOME20"
                    required
                    disabled={!!editingCoupon}
                  />
                </div>
                <div className="form-field">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Welcome Discount"
                    required
                  />
                </div>
              </div>

              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Coupon description shown to customers"
                  rows="2"
                />
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Discount Type *</label>
                  <select
                    value={form.benefitType}
                    onChange={(e) => setForm({ ...form, benefitType: e.target.value })}
                  >
                    <option value="PERCENTAGE_DISCOUNT">Percentage Discount</option>
                    <option value="FLAT_DISCOUNT">Flat Discount</option>
                  </select>
                </div>
                {form.benefitType !== 'FREE_DELIVERY' && (
                  <div className="form-field">
                    <label>{form.benefitType === 'PERCENTAGE_DISCOUNT' ? 'Percentage (%)' : 'Amount (₹)'} *</label>
                    <input
                      type="number"
                      value={form.benefitValue}
                      onChange={(e) => setForm({ ...form, benefitValue: e.target.value })}
                      placeholder={form.benefitType === 'PERCENTAGE_DISCOUNT' ? 'e.g. 20' : 'e.g. 500'}
                      min="0"
                      step="0.01"
                      required={!form.freeDelivery}
                    />
                  </div>
                )}
                {form.benefitType === 'PERCENTAGE_DISCOUNT' && (
                  <div className="form-field">
                    <label>Max Discount (₹)</label>
                    <input
                      type="number"
                      value={form.benefitMaxValue}
                      onChange={(e) => setForm({ ...form, benefitMaxValue: e.target.value })}
                      placeholder="e.g. 1000"
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
              </div>

              <div className="form-field checkbox-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.freeDelivery}
                    onChange={(e) => setForm({ ...form, freeDelivery: e.target.checked })}
                  />
                  <span className="checkbox-text">Include Free Delivery</span>
                </label>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Valid From</label>
                  <input
                    type="datetime-local"
                    value={form.validFrom}
                    onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Valid Until</label>
                  <input
                    type="datetime-local"
                    value={form.validUntil}
                    onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Max Total Uses</label>
                  <input
                    type="number"
                    value={form.maxTotalUses}
                    onChange={(e) => setForm({ ...form, maxTotalUses: e.target.value })}
                    placeholder="Unlimited"
                    min="1"
                  />
                </div>
                <div className="form-field">
                  <label>Max Uses Per User</label>
                  <input
                    type="number"
                    value={form.maxUsesPerUser}
                    onChange={(e) => setForm({ ...form, maxUsesPerUser: e.target.value })}
                    placeholder="1"
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Min Order Amount (₹)</label>
                  <input
                    type="number"
                    value={form.minOrderAmount}
                    onChange={(e) => setForm({ ...form, minOrderAmount: e.target.value })}
                    placeholder="No minimum"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-field">
                  <label>Priority</label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="modal-btn cancel" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="modal-btn confirm-save" disabled={saving}>
                  {saving ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Coupon</h3>
            <p className="modal-warning">
              Are you sure you want to delete coupon <strong>{showDeleteConfirm.code}</strong>? This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={() => setShowDeleteConfirm(null)}>
                Cancel
              </button>
              <button className="modal-btn confirm-cancel" onClick={() => handleDelete(showDeleteConfirm.id)}>
                Delete Coupon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
